import type { SurfaceParams } from './surfaces';
import type { TyreSpec } from './types';
import { DEG, clamp } from './math';

/**
 * Combined-slip tyre model: a Pacejka Magic Formula curve evaluated on *normalised* slip.
 *
 *   sx = κ / κ_peak,  sy = tan(α) / tan(α_peak),  s = |(sx, sy)|
 *   F  = µ(Fz) · Fz · MF(s),   MF(s) = sin(C · atan(B · s)),  MF(1) = 1 (peak at s = 1)
 *   Fx = F · sx / s,  Fy = -F · sy / s · lateralRatio
 *
 * C follows from the sliding/peak ratio (falloff): sin(C·π/2) = falloff. B = tan(π / 2C) puts the
 * peak at s = 1. A loose surface has a high falloff (flat after the peak), so a held slide keeps
 * most of its grip — the core of gravel driving. Load sensitivity lowers µ as Fz rises.
 */
export interface CurveCoeffs {
  B: number;
  C: number;
}

export function curveFor(falloff: number): CurveCoeffs {
  const f = clamp(falloff, 0.3, 0.995);
  const C = 2 - (2 * Math.asin(f)) / Math.PI;
  const B = Math.tan(Math.PI / (2 * C));
  return { B, C };
}

export function magic(s: number, k: CurveCoeffs): number {
  return Math.sin(k.C * Math.atan(k.B * s));
}

/** dMF/ds at s (for the implicit wheel-spin linearisation). */
export function magicSlope(s: number, k: CurveCoeffs): number {
  const bs = k.B * s;
  return Math.cos(k.C * Math.atan(bs)) * k.C * k.B / (1 + bs * bs);
}

export interface TyreSurfaceCoeffs {
  mu: number;
  slipPeak: number;
  tanAlphaPeak: number;
  curve: CurveCoeffs;
}

/** Combine surface parameters with the tyre compound's multipliers. */
export function tyreOnSurface(t: TyreSpec, s: SurfaceParams): TyreSurfaceCoeffs {
  const g = t.surfaceGrip[s.id] ?? 1;
  const p = t.surfaceSlip[s.id] ?? 1;
  return {
    mu: s.mu * g,
    slipPeak: s.slipPeak * p,
    tanAlphaPeak: Math.tan(s.slipAnglePeakDeg * DEG * p),
    curve: curveFor(s.falloff),
  };
}

export interface TyreForce {
  fx: number;
  fy: number;
  /** dFx/dκ at the operating point, for the implicit wheel integration (>= 0). */
  dFxdK: number;
  /** Combined normalised slip magnitude (1 = at the peak). */
  slip: number;
}

/** Effective friction coefficient with load sensitivity. */
export function loadedMu(mu: number, fz: number, t: TyreSpec): number {
  const r = fz / t.nominalLoad - 1;
  return mu * clamp(1 - t.loadSensitivity * r, 0.55, 1.25);
}

/**
 * Forces for slip ratio `kappa` and lateral slip `tanAlpha` (= v_lat / |v_long|, positive when the
 * contact patch slides to the car's left). Writes into `out`.
 */
export function tyreForces(out: TyreForce, kappa: number, tanAlpha: number, fz: number, c: TyreSurfaceCoeffs, t: TyreSpec): TyreForce {
  if (fz <= 0) {
    out.fx = 0;
    out.fy = 0;
    out.dFxdK = 0;
    out.slip = 0;
    return out;
  }
  const mu = loadedMu(c.mu, fz, t);
  const sx = kappa / c.slipPeak;
  const sy = tanAlpha / c.tanAlphaPeak;
  const s = Math.sqrt(sx * sx + sy * sy);
  const fmax = mu * fz;
  if (s < 1e-9) {
    const slope = (c.curve.B * c.curve.C * fmax) / c.slipPeak;
    out.fx = 0;
    out.fy = 0;
    out.dFxdK = slope;
    out.slip = 0;
    return out;
  }
  const f = fmax * magic(s, c.curve);
  const fx = (f * sx) / s;
  const fy = (-f * sy) / s * t.lateralGripRatio;
  // Derivative of Fx with respect to κ along the pure-longitudinal direction, approximated by the
  // secant/tangent blend; clamp to positive so the implicit term only ever adds stiffness.
  const tangent = (fmax * magicSlope(s, c.curve)) / c.slipPeak;
  const secant = f / s / c.slipPeak;
  const w = (sx * sx) / (s * s);
  out.fx = fx;
  out.fy = fy;
  out.dFxdK = Math.max(0, w * tangent + (1 - w) * secant);
  out.slip = s;
  return out;
}
